import clsx from "clsx";
import PerfectScrollbar from "react-perfect-scrollbar";
import { EconomicCalendarItem } from "./EconomicCalendarItem";
import { ImpactLevelSelect } from "./ImpactLevelSelect";
import { CountrySelect } from "./CountrySelect";
import CloseIcon from "@mui/icons-material/Close";
import { useEconomicCalendar } from "../hooks/useEconomicCalendar";
import { NoResultsFound } from "@/components/Core/NoResults/NoResultsFound";
import { formatDate } from "@/utils/dateTime";
import { useRef } from "react";
import { useOnClickOutside } from "usehooks-ts";
import { CalendarSkeletonLoader } from "./SkeletonLoader";
import styles from "./EconomicCalendar.module.scss";

export const EconomicCalendar = ({ onClose }: { onClose: () => void }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const {
        data,
        selectedCountry,
        selectedImpactLevel,
        setCountry,
        setImpactLevel,
        isLoading,
        countries,
    } = useEconomicCalendar();

    useOnClickOutside(containerRef, event => {
        const target = event.target as HTMLElement;

        if (`${(target as any).id}`.includes("economicCalendarBtn")) {
            return;
        }
        if (containerRef.current?.contains(target)) return;
        const isSelectDropdown = (target as HTMLElement).closest(
            ".MuiPopover-root, .MuiAutocomplete-popper"
        );
        if (isSelectDropdown) return;
        onClose();
    });

    return (
        <div className={styles.container} ref={containerRef}>
            <CloseIcon onClick={onClose} className={styles.closeIcon} />
            {isLoading && <CalendarSkeletonLoader />}
            {!isLoading && (
                <>
                    <div className={styles.filters}>
                        <ImpactLevelSelect
                            onChange={setImpactLevel}
                            selectedValue={selectedImpactLevel}
                        />
                        <CountrySelect
                            onChange={setCountry}
                            selectedValue={selectedCountry}
                            countries={countries}
                        />
                    </div>
                    <div className={styles.content}>
                        <div className={styles.headers}>
                            <div className={styles.tab}>Actual</div>
                            <div className={styles.tab}>Estimate</div>
                            <div className={styles.tab}>Previous</div>
                        </div>
                        <div className={clsx(styles.itemsList, styles.scrollBarOverride)}>
                            <PerfectScrollbar
                                options={{ suppressScrollX: true, useBothWheelAxes: false }}>
                                {!data.length && <NoResultsFound type="search" text="No results" />}
                                {data.map((item, index) => {
                                    const currentItemDate = formatDate(item.time, "dddd, MMMM D, YYYY");
                                    const previousItemDate =
                                        index > 0
                                            ? formatDate(data[index - 1]?.time, "dddd, MMMM D, YYYY")
                                            : null;

                                    if (currentItemDate !== previousItemDate) {
                                        return (
                                            <div key={index} className={styles.block}>
                                                <div className={styles.datetime}>{currentItemDate}</div>
                                                <EconomicCalendarItem data={item} key={index} />
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={index} className={styles.block}>
                                            <EconomicCalendarItem data={item} key={index} />
                                        </div>
                                    );
                                })}
                            </PerfectScrollbar>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
